import React from "react";
import { ChevronLeft, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface SessionHeaderProps {
  clientName: string;
  sessionDate: string;
  templateType?: string;
  status?: string;
  onBack: () => void;
  onDelete: () => void;
}

const SessionHeader: React.FC<SessionHeaderProps> = ({
  clientName,
  sessionDate,
  templateType,
  status,
  onBack,
  onDelete
}) => {
  const getStatusDisplay = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return {
          icon: '✓',
          text: 'Completed',
          className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
        };
      case 'processing':
        return {
          icon: <Loader2 className="h-3 w-3 animate-spin" />,
          text: 'Processing',
          className: 'bg-amber-50 text-amber-700 border-amber-200'
        };
      case 'failed':
        return {
          icon: '×',
          text: 'Failed',
          className: 'bg-red-50 text-red-700 border-red-200'
        };
      case 'pending':
        return {
          icon: '○',
          text: 'Pending',
          className: 'bg-slate-50 text-slate-600 border-slate-200'
        };
      default:
        return {
          icon: '○',
          text: status || 'Unknown',
          className: 'bg-slate-50 text-slate-600 border-slate-200'
        };
    }
  };

  const statusDisplay = status ? getStatusDisplay(status) : null;

  return (
    <div className="bg-white border-b border-gray-100">
      {/* Full-width top navigation */}
      <div className="px-6 py-6">
        <div className="flex justify-between items-center mb-8">
          {/* Back Navigation - True top left */}
          <button 
            onClick={onBack}
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors group"
          >
            <ChevronLeft className="h-5 w-5 mr-2 transition-transform group-hover:-translate-x-0.5" />
            <span className="text-base font-medium">Back to client</span>
          </button>
          
          {/* Delete Button - True top right */}
          <button
            onClick={onDelete}
            className="inline-flex items-center text-gray-400 hover:text-red-500 hover:bg-red-50 px-3 py-2 rounded-lg transition-all duration-200"
            title="Delete Session"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
        
        {/* Centered Content - with max-width only for content */}
        <div className="max-w-4xl mx-auto text-center">
          {/* Removed absolute positioning for delete button */}
          
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {clientName}
          </h1>
          
          <p className="text-gray-500 mb-4">
            {sessionDate}
          </p>
          
          {(templateType || statusDisplay) && (
            <div className="flex items-center justify-center gap-3">
              {templateType && (
                <Badge variant="outline" className="text-xs font-medium bg-gray-50 text-gray-700 border-gray-200">
                  {templateType.toUpperCase()}
                </Badge>
              )}
              
              {statusDisplay && (
                <Badge className={`text-xs font-medium border flex items-center gap-1.5 ${statusDisplay.className}`}>
                  <span className="text-xs">{statusDisplay.icon}</span>
                  <span>{statusDisplay.text}</span>
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionHeader;